import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

type ErrorMessageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  errors: any;
};
export default function ShowErrorMessageModal({
  isOpen,
  onClose = () => {
    // No-op: Default implementation
  },
  errors,
}: ErrorMessageModalProps) {
  const classes = styles();
  const { t } = useTranslation();
  const errorsData: any = [];
  const populateErrorMessages = errorLoop => {
    let isValid = '';
    let isRequired = '';
    let message = '';
    Array.isArray(errorLoop) &&
      errorLoop?.forEach(errorMsg => {
        if (errorMsg.name) {
          if (errorMsg.name !== 'minProperties') {
            let fieldKeyword = '';
            if (errorMsg?.path?.length) {
              fieldKeyword = 'instance.' + errorMsg?.path[0];
            } else if (errorMsg.argument) {
              fieldKeyword = 'instance.' + errorMsg.argument;
            } else {
              fieldKeyword = errorMsg.property;
            }
            if (fieldKeyword.includes('TimeOpen')) {
              fieldKeyword = fieldKeyword.substring(0, fieldKeyword.length - 4);
            } else if (fieldKeyword.includes('TimeClose')) {
              fieldKeyword = fieldKeyword.substring(0, fieldKeyword.length - 5);
            }
            if (errorMsg.name === 'pattern') {
              if (!isValid.includes(t('operationHours.' + fieldKeyword)))
                isValid = isValid
                  ? isValid + ', ' + t('operationHours.' + fieldKeyword)
                  : t('operationHours.' + fieldKeyword);
            } else {
              if (!isRequired.includes(t('operationHours.' + fieldKeyword)))
                isRequired = isRequired
                  ? isRequired + ', ' + t('operationHours.' + fieldKeyword)
                  : t('operationHours.' + fieldKeyword);
            }
          }
        } else {
          if (errorMsg.restaurantNumber)
            message += t('RestaurantNumberSearch') + errorMsg.restaurantNumber;
          message = message
            ? message + ', ' + t('operationHours.' + errorMsg.property)
            : t('operationHours.' + errorMsg.property);
        }
      });
    if (isValid || isRequired || message) {
      isValid = isValid
        ? t('operationHours.errorModal.valid') + ' ' + isValid
        : '';
      const checkLineBreak = isValid && isRequired ? '\n' : '';
      isRequired = isRequired
        ? isRequired + ' ' + t('operationHours.errorModal.mandatory')
        : '';
      message = message ? message : '';
      return isValid + checkLineBreak + isRequired + message;
    } else {
      return t('operationHours.massUpload.fail');
    }
  };

  if (
    Array.isArray(errors?.error_description) &&
    errors?.error_description?.length
  ) {
    const isMassUpload = errors?.error_description.some(obj =>
      Object.keys(obj).includes('error_message'),
    );
    if (!isMassUpload) {
      errorsData[0] = {
        error_message: populateErrorMessages(errors?.error_description),
      };
    } else {
      errors?.error_description?.forEach((errorObj, index) => {
        errorsData[index] = {
          row: errorObj.row,
        };
        errorsData[index]['error_message'] = populateErrorMessages(
          errorObj?.error_message,
        );
      });
    }
  }

  const columns = [
    {
      id: 'row',
      label: t('operationHours.column.rowNumbers'),
      minWidth: 159,
      maxWidth: 180,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 1,
      paddingRight: 0,
    },
    {
      id: 'error_message',
      label: t('operationHours.column.error'),
      minWidth: 70,
      maxWidth: 159,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 1,
      paddingRight: 0,
    },
  ];

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      fullWidth
      style={{ maxHeight: '80vh' }}
      maxWidth="md"
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle id="customized-dialog-title" className={classes.errorModal}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {t('operationHours.errorModal.Header')}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton style={{ padding: 0 }} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className={`${classes.tabPanel} ${classes.errorRoot}`}>
        <Grid
          container
          direction="row"
          justify="center"
          alignItems="center"
          alignContent="stretch"
          wrap="nowrap"
        >
          <Grid item md={12} lg={12}>
            <div className={classes.errorContainer}>
              <div className={classes.errorAppBar}>
                <Typography className={classes.errorTitle}>
                  {t('operationHours.errorModal.title')}
                </Typography>
              </div>
            </div>
          </Grid>
        </Grid>
        <Box marginTop={5}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead style={{ backgroundColor: 'red !important' }}>
                <TableRow>
                  {columns.map(column => (
                    <TableCell
                      key={column.id}
                      style={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        paddingTop: column.paddingTop,
                        paddingBottom: column.paddingBottom,
                        paddingLeft: column.paddingLeft,
                        paddingRight: column.paddingRight,
                        textTransform: 'capitalize',
                        fontSize: 18,
                      }}
                    >
                      <b>{column.label}</b>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(errorsData) && errorsData.length ? (
                  errorsData?.map((errorObj, index) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                        {columns.map(column => {
                          return (
                            <TableCell
                              key={column.id}
                              style={{
                                padding: '10px 0px',
                                borderBottom: '1px solid rgb(155 149 149)',
                              }}
                            >
                              <div
                                className={classes.lineBreak}
                                style={{ textAlign: 'left' }}
                              >
                                {column.id === 'row' && !errorObj[column.id]
                                  ? index + 1
                                  : errorObj[column.id]}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={42}>
                      <div style={{ textAlign: 'center' }}>
                        <Typography>
                          {' '}
                          {t('operationHours.massUpload.fail')}
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions className={classes.tabPanel}>
        <Button
          variant="contained"
          className={classes.errorButton}
          color="primary"
          autoFocus
          onClick={onClose}
        >
          {t('app.common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
